import { buildUploadUrl } from "@/shared/models/Inspection";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { Box, IconButton, Typography } from "@mui/material";
import type { ICellRendererParams } from "ag-grid-community";
import { useState } from "react";
import { InspectionReportSheetDetail } from "../models/InspectionReport";
import { buildReadings, useInspection } from "../sheet/InspectionSheetPage";
import { InspectionSheetTable } from "../sheet/components/InspectionSheetTable";
import { DrawingModal } from "./DrawingModal";

type InspectedSheet = {
	InspectionId: number;
	SectionId: number | null;
	SectionName?: string;
	Rev?: number;
	DrawingPath?: string | null;
	DrawingFileName?: string | null;
};

type Props = ICellRendererParams<InspectedSheet> & {
	sheetDetails?: Map<number, InspectionReportSheetDetail>;
};

export const InspectionDetailPanel = ({ data, sheetDetails }: Props) => {
	const [error, setError] = useState("");
	const [isDrawingOpen, setIsDrawingOpen] = useState(false);

	const preloaded = sheetDetails?.get(data?.InspectionId ?? 0);

	const { data: detail, isLoading } = useInspection({
		inspectionId: preloaded ? 0 : (data?.InspectionId ?? 0),
		onError: (queryError) => setError(queryError.message),
	});

	const columns = preloaded ? preloaded.columns : (detail?.columns ?? []);
	const readings = preloaded ? preloaded.readings : buildReadings(detail);

	const sectionName =
		data?.SectionName ?? detail?.inspection?.SectionName ?? "";
	const rev = data?.Rev ?? detail?.inspection?.Rev ?? null;
	const drawingUrl = buildUploadUrl(
		data?.DrawingPath ?? detail?.inspection?.DrawingPath ?? null,
	);
	const drawingFileName =
		data?.DrawingFileName ?? detail?.inspection?.DrawingFileName ?? null;

	if (error)
		return (
			<Box sx={{ p: 2 }}>
				<Typography variant="body2" color="error">
					{error}
				</Typography>
			</Box>
		);

	return (
		<Box
			sx={{
				minHeight: "15vh",
				maxHeight: "45vh",
				p: 1,
				boxSizing: "border-box",
				display: "flex",
				gap: 1,
			}}
		>
			<Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
				<InspectionSheetTable
					columns={columns}
					sectionId={data?.SectionId ?? null}
					readings={readings}
					draft={null}
					editingRowId={null}
					isLoading={!preloaded && isLoading}
					readOnly
					showActions={false}
					showSummary={false}
					compact={drawingUrl !== null}
				/>
			</Box>

			{drawingUrl && (
				<Box
					sx={{
						position: "relative",
						flex: "0 0 auto",
						width: "26%",
						minWidth: 190,
						maxWidth: 340,
						display: { xs: "none", md: "flex" },
						alignItems: "center",
						justifyContent: "center",
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 1,
						backgroundColor: "#fff",
					}}
				>
					<Box
						component="img"
						src={drawingUrl}
						alt={`${sectionName} profile drawing`}
						title={drawingFileName ?? "Profile drawing"}
						onClick={() => setIsDrawingOpen(true)}
						sx={{
							maxWidth: "100%",
							maxHeight: "100%",
							objectFit: "contain",
							cursor: "zoom-in",
							p: 0.5,
						}}
					/>
					<IconButton
						size="small"
						title="Expand"
						onClick={() => setIsDrawingOpen(true)}
						sx={{
							position: "absolute",
							top: 2,
							right: 2,
							backgroundColor: "background.paper",
							border: "1px solid",
							borderColor: "divider",
							"&:hover": { backgroundColor: "background.paper" },
						}}
					>
						<OpenInFullIcon sx={{ fontSize: 14 }} />
					</IconButton>
				</Box>
			)}

			<DrawingModal
				open={isDrawingOpen}
				url={drawingUrl}
				sectionName={sectionName}
				rev={rev}
				fileName={drawingFileName}
				onClose={() => setIsDrawingOpen(false)}
			/>
		</Box>
	);
};
